import React, { useRef, type MouseEvent as ReactMouseEvent, type RefObject } from 'react';
import { useTranslation } from 'react-i18next';
import { Copy, ChevronDown, ChevronUp, ObjectGroup, Trash2 } from '../emailBuilderIcons';
import { canGroupRows } from '../domain/mutations/group-rows';
import { getMultiSelectRowCapabilities, resolveOrderedSiblingRowIds } from '../domain/selection/row-bulk-ops';
import { selectDocument, selectSelectedRowIds, selectSelection } from '../domain/selectors';
import { useEditorActions, useEditorStore } from '../domain/store';
import { EMAIL_BUILDER_ACTION_RAIL_ATTR } from './actionRailConstants';
import {
  emailBuilderCanvasBlockRailActionTooltip,
  emailBuilderCanvasBlockReorderRailTooltip,
} from '../editing/emailBuilderCanvasBlockInteractions';
import { emailBuilderRowGroupRailActionTooltip } from '../shell/emailBuilderRowGroupHotkeys';
import { deleteSelectionWithConfirm } from '../lib/deleteSelection';
import { useConfirm } from '../shell/EmailBuilderConfirmProvider';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { useStableScopedDomId } from '../useStableDomId';
import { useEmailBuilderVerticalActionRailPin } from './useEmailBuilderVerticalActionRailPin';
import {
  emailBuilderVerticalActionRailClassName,
  emailBuilderVerticalActionRailPinStyle,
} from './emailBuilderVerticalActionRailChrome';

function isMacPlatform(): boolean {
  return (
    typeof navigator !== 'undefined' &&
    /Mac|iPhone|iPad|iPod/.test(navigator.userAgent)
  );
}

type RowMultiSelectRailButtonProps = {
  action: string;
  tooltip: string;
  ariaLabel: string;
  disabled?: boolean;
  danger?: boolean;
  onClick: () => void;
  children: React.ReactNode;
};

function RowMultiSelectRailButton({
  action,
  tooltip,
  ariaLabel,
  disabled = false,
  danger = false,
  onClick,
  children,
}: RowMultiSelectRailButtonProps) {
  const tooltipId = useStableScopedDomId('email-builder-row-multi-select-rail', action);

  const button = (
    <button
      type="button"
      className={"email-builder-canvas-rail-btn"}
      data-danger={danger ? 'true' : undefined}
      aria-label={ariaLabel}
      disabled={disabled}
      onClick={onClick}>
      {children}
    </button>
  );

  return (
    <OverlayTrigger
      placement="right"
      flip
      delay={{ show: 400, hide: 100 }}
      overlay={<Tooltip id={tooltipId}>{tooltip}</Tooltip>}>
      {disabled ? <span className={"email-builder-canvas-rail-tooltip-target"}>{button}</span> : button}
    </OverlayTrigger>
  );
}

type RowMultiSelectActionRailProps = {
  frameRef?: RefObject<HTMLElement | null>;
  onClaimHover?: (event: ReactMouseEvent<HTMLElement>) => void;
};

export function RowMultiSelectActionRail({
  frameRef,
  onClaimHover,
}: RowMultiSelectActionRailProps) {
  const { t } = useTranslation();
  const document = useEditorStore(selectDocument);
  const selection = useEditorStore(selectSelection);
  const selectedRowIds = useEditorStore(selectSelectedRowIds);
  const { groupRows, duplicateSelected, deleteSelected, deleteGroup, moveSelected } =
    useEditorActions();
  const { confirm } = useConfirm();
  const railRef = useRef<HTMLDivElement>(null);
  const portaled = frameRef !== undefined;
  const pinStyle = useEmailBuilderVerticalActionRailPin({
    enabled: portaled,
    frameRef,
    railRef,
    useRowGroupAnchor: true,
  });

  const capabilities = getMultiSelectRowCapabilities(document, selectedRowIds);
  const groupable = canGroupRows(document, selectedRowIds);
  const rowScope = resolveOrderedSiblingRowIds(document, [...selectedRowIds]);
  const showGroupButton = rowScope?.list.parent !== 'group';

  if (!capabilities || selectedRowIds.length < 2) {
    return null;
  }

  const { canDuplicate, canDelete, canUp, canDown, showMoveButtons, upLabel, downLabel } =
    capabilities;
  const isMac = isMacPlatform();
  const duplicateTooltip = emailBuilderCanvasBlockRailActionTooltip(
    'duplicate',
    t('Duplicate'),
    isMac,
  );
  const deleteTooltip = !canDelete
    ? t("Can't delete the last row")
    : emailBuilderCanvasBlockRailActionTooltip(
        'delete',
        t('Delete'),
        isMac,
      );
  const groupTooltip = groupable
    ? emailBuilderRowGroupRailActionTooltip('group', t('Group rows'), isMac)
    : t('Select adjacent rows to group');
  const groupAriaLabel = groupable
    ? groupTooltip
    : t('Group rows — select adjacent rows to group');

  function handleDelete() {
    void deleteSelectionWithConfirm(
      document,
      selection,
      { confirm },
      { deleteSelected, deleteGroup },
      t,
    );
  }

  return (
    <div
      ref={railRef}
      className={emailBuilderVerticalActionRailClassName(portaled)}
      data-editor-chrome
      role="toolbar"
      aria-label={t('Selected rows')}
      {...{ [EMAIL_BUILDER_ACTION_RAIL_ATTR]: '' }}
      style={emailBuilderVerticalActionRailPinStyle(portaled, pinStyle)}
      onMouseOver={onClaimHover}
      onPointerDown={event => event.stopPropagation()}
      onClick={event => event.stopPropagation()}>
      {showMoveButtons ? (
        <>
          <RowMultiSelectRailButton
            action="move-back"
            tooltip={emailBuilderCanvasBlockReorderRailTooltip('move-earlier', t(upLabel), isMac)}
            ariaLabel={emailBuilderCanvasBlockReorderRailTooltip('move-earlier', t(upLabel), isMac)}
            disabled={!canUp}
            onClick={() => moveSelected(-1)}>
            <ChevronUp size={16} aria-hidden />
          </RowMultiSelectRailButton>
          <RowMultiSelectRailButton
            action="move-forward"
            tooltip={emailBuilderCanvasBlockReorderRailTooltip('move-later', t(downLabel), isMac)}
            ariaLabel={emailBuilderCanvasBlockReorderRailTooltip('move-later', t(downLabel), isMac)}
            disabled={!canDown}
            onClick={() => moveSelected(1)}>
            <ChevronDown size={16} aria-hidden />
          </RowMultiSelectRailButton>
        </>
      ) : null}
      {showGroupButton ? (
        <RowMultiSelectRailButton
          action="group"
          tooltip={groupTooltip}
          ariaLabel={groupAriaLabel}
          disabled={!groupable}
          onClick={() => groupRows([...selectedRowIds])}>
          <ObjectGroup size={16} aria-hidden />
        </RowMultiSelectRailButton>
      ) : null}
      <RowMultiSelectRailButton
        action="duplicate"
        tooltip={duplicateTooltip}
        ariaLabel={duplicateTooltip}
        disabled={!canDuplicate}
        onClick={duplicateSelected}>
        <Copy size={16} aria-hidden />
      </RowMultiSelectRailButton>
      <RowMultiSelectRailButton
        action="delete"
        tooltip={deleteTooltip}
        ariaLabel={deleteTooltip}
        danger
        disabled={!canDelete}
        onClick={handleDelete}>
        <Trash2 size={16} aria-hidden />
      </RowMultiSelectRailButton>
    </div>
  );
}
